import { useTranslation } from "react-i18next";
import TutorialProgress from "./TutorialProgress";

interface TutorialCardProps {
  tutorial: any;
  language: string;
  getUserTutorialProgress: (tutorialId: string) => any;
  onTutorialClick: (tutorial: any) => void;
  partNumber: number;
}

const TutorialCard = ({
  tutorial,
  language,
  getUserTutorialProgress,
  onTutorialClick,
  partNumber,
}: TutorialCardProps) => {
  const { t } = useTranslation();

  const tutorialProgress = getUserTutorialProgress(tutorial._id);

  return (
    <div
      className="group rounded-lg sm:rounded-xl border border-gray-200 bg-white shadow-sm hover:shadow-lg transition-all duration-300 dark:border-gray-700 dark:bg-gray-800 overflow-hidden cursor-pointer transform hover:-translate-y-1"
      onClick={() => onTutorialClick(tutorial)}
    >
      <div className="relative h-40 sm:h-48 overflow-hidden bg-gray-100 dark:bg-gray-700">
        {tutorial.thumbnail ? (
          <img
            src={`${tutorial.thumbnail}`}
            alt={tutorial.title?.[language] || tutorial.title?.en || "Untitled"}
            className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400">
            <svg
              className="w-10 h-10 sm:w-12 sm:h-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3">
          <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-lg px-2 sm:px-3 py-1.5 sm:py-2">
            <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
              {t("courses.duration")}: {tutorial.duration}{" "}
              {t("courses.minutes")}
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 sm:p-6">
        {/* Tutorial Progress Indicators */}
        <TutorialProgress
          tutorialProgress={tutorialProgress}
          t={t}
          partNumber={partNumber}
        />

        <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
          {tutorial.title?.[language] || tutorial.title?.en || "Untitled"}
        </h3>
        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2 sm:line-clamp-3">
          {tutorial.description?.[language] || tutorial.description?.en || ""}
        </p>

        {/* Tutorial Actions */}
        <div className="mt-3 sm:mt-4 flex items-center justify-between">
          <div className="flex items-center space-x-1.5 sm:space-x-2">
            <svg
              className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400">
              {tutorial.slides?.length || 0} {t("courses.slides")}
            </span>
          </div>
          <div className="flex items-center text-primary-600 dark:text-primary-400 text-xs sm:text-sm font-medium">
            <span className="hidden sm:inline">{t("courses.startTutorial")}</span>
            <span className="sm:hidden">{t("courses.start")}</span>
            <svg
              className="w-3.5 h-3.5 sm:w-4 sm:h-4 ml-1 group-hover:translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TutorialCard;
